/** Wire revision; session joins require the current version (close 4409 otherwise). */
export const PROTOCOL_VERSION = 58;

/**
 * Explicit bundle build compatibility, not session or machine-channel negotiation.
 * Protocol 57 requires independently identified terminal views and completion-only parser credit.
 * The explicit-view attach/detach/ack SDK and plugin ABI requires rebuilt bundles; no shared-view
 * fallback is safe. Protocol 58 adds the optional manifest `contributes.creators` and the
 * `folderId` beside `core.index.createFolder`'s index. A 57 bundle is expected to load unchanged,
 * but no proof from released 57 artifacts against this host exists yet, so acceptance resets
 * to 58 and an installed 57 bundle is held `repack_required` until rebuilt or proven.
 * Machine/instance wires and independent native/enrollment floors are unchanged.
 * Retain a prior stamp only after proving its unchanged artifacts against the host;
 * reset on an incompatible plugin ABI change. Do not infer a numeric version range.
 */
export const PLUGIN_BUNDLE_PROTOCOL_COMPAT_VERSIONS: ReadonlySet<string> = new Set(["58"]);

/**
 * Machine-channel acceptance set. Agents are long-lived (they hold PTYs and
 * survive server deploys), so the machine channel accepts every protocol
 * version whose agent-facing wire (AgentMessage/ServerToAgentMessage) and
 * adoption semantics are identical to the current one. Session (browser)
 * joins stay strictly current — the server always serves the matching SPA.
 *
 * Discipline (docs/CONTRACTS.md §Protocol and compatibility): a bump that leaves the agent wire
 * identical ADDS the new version here. A bump whose agent-wire change is
 * strictly additive-optional (every pre-bump frame still parses, and the
 * server's default for the absent field reproduces pre-bump semantics) also
 * ADDS — forcing a fleet restart for a change that locks nobody out would be
 * the exact failure the invariant guards against. Any other agent-wire change
 * RESETS the set to only the new version and requires a coordinated fleet
 * restart (server + spokes together).
 *
 * A resource-admission bound is not a wire revision (#403, decision 2026-09-26). The hub
 * refuses a hello advertising more than 1,024 terminals or a duplicate terminal id at EVERY
 * accepted version, before authentication or reconciliation; every compliant frame is
 * byte-identical and keeps its meaning, so neither the version nor either set changes.
 *
 * HISTORY. Written in the CURRENT lexicon, because a changelog that speaks a
 * retired vocabulary is a second door onto the concepts it describes: what v9
 * called `SessionInfo.padId` is the field this tree calls
 * `TerminalInfo.containerId`, and the frame v10 called `session_event` is the
 * one now called `terminal_event`. The wire bytes of a past version are not
 * rewritten by naming them in today's words — only v16's are, below.
 *
 * v2 -> v3: session-channel only (init/resync gained selfCaps); agent wire
 * verified identical, so v2 agents stay accepted.
 * v3 -> v4: agent wire gained OPTIONAL `exitCode` on advertised terminals
 * (hello). Absence ≡ the old `null` adoption semantics, so v2/v3 agents stay
 * accepted; they merely keep reporting disconnect-window exits as null.
 * v4 -> v5: session-channel scene records became strict native terminal
 * records. The machine wire is identical, so v2/v3/v4 agents stay accepted.
 * v5 -> v6: session-channel scene frames became Yjs document updates. The
 * machine wire is byte-identical, so existing agents stay accepted.
 * v6 -> v7: session-channel attendance states gained required `connIds` so
 * viewers can retire a closed tab's cursor. The machine wire is byte-identical,
 * so existing agents stay accepted.
 * v7 -> v8: session-channel cursor frames dropped the never-rendered `tool`
 * field. The machine wire is byte-identical, so existing agents stay accepted.
 * v8 -> v9: session-channel TerminalInfo.containerId became nullable and the
 * terminal event frame gained "parked". The machine wire is byte-identical, so
 * existing agents stay accepted.
 * v9 -> v10: session-channel TerminalInfo gained a nullable name and the
 * terminal event frame gained "renamed". The machine wire is byte-identical, so
 * existing agents stay accepted.
 * v10 -> v11: containers gained a discipline and transient flag, and
 * compositions store a layout tree; scene elements gained portal; the session
 * `join` frame gained an OPTIONAL `spectator` flag so a portal's preview socket
 * can watch a room without occupying it (absent ≡ occupant, the pre-flag
 * semantics); `terminal_open` gained an OPTIONAL `placement: "tile"` (absent ≡
 * the opener authors a canvas element) and `terminal_opened` an OPTIONAL `ref`
 * echoing it, so a composition can birth a terminal the server places as a
 * tile; TerminalInfo and the per-container terminal summary DROPPED
 * `elementId`, because a terminal holds many placements at once (canvas mirrors,
 * tile leaves) and consumers read placement from the doc's elements and layout
 * tree instead. The machine wire is byte-identical, so existing agents stay
 * accepted.
 * v11 -> v12: session-channel frames became MULTIPLEXED — every channel-level frame
 * carries a `ch` routing id, `join`/`leave` are per-channel ops on one socket, and
 * per-channel epoch/resume hints ride each channel's own join, so one tab holds exactly
 * one TCP connection no matter how many rooms it renders. ping/pong stay
 * connection-level. The machine wire is byte-identical, so existing agents stay
 * accepted.
 * v12 -> v13: session-channel only. Tile placement destinations gained an OPTIONAL
 * `between` flag (absent ≡ split-the-target, the pre-bump semantics) so a seam-band
 * drop can mean "wedge between both neighbors" on the wire; gesture `carry` payloads
 * gained an OPTIONAL `aim` so collaborators can re-derive a producer's live split
 * preview. The machine wire is byte-identical, so existing agents stay accepted.
 * v13 -> v14: session/HTTP only — connection-level plugins frame, presence
 * vantage/spotlight, panel tile ref, plugins:manage cap, action/resolve doors,
 * and gesture `carry` payloads gained a REQUIRED `item`: a carry now names the
 * item its ref addresses, so a collaborator paints legality from the frame
 * instead of re-resolving an address against its own index poll.
 * Machine wire byte-identical, so existing agents stay accepted.
 * v14 -> v15: session/HTTP only — the plugin behavioral contract. Manifests gained
 * OPTIONAL `dependencies`/`after` (absent ≡ no relationships and no ordering constraint,
 * the v14 semantics), `dataVersion` (absent ≡ unversioned: nothing migrates, nothing
 * refuses), `dormant` (absent ≡ the engine's named ghost, which is what v14 already drew),
 * `purges` (an audit-visible declaration bound to no verb) and per-element `placement`
 * traits (absent ≡ canvas_item/inline, exactly today's contributed element); roster rows
 * gained `lifecycle`, `refusal` and `changedBy`/`changedAt` (all absent ≡ a plugin nobody
 * has toggled and nothing refuses) and their `source` widened from the literal "builtin"
 * to "builtin" | "plugin", which every v14 value still satisfies. Published actions gained
 * `scope` ∈ {workspace, container}, DEFAULTED to `workspace`, which is the v14 rule
 * verbatim: a container-scoped token is refused every action that does not declare itself
 * confined to one container. All of it rides `GET /api/plugins`, `GET /api/protocol` and
 * the connection-level `plugins` frame. The machine wire is byte-identical — an agent
 * never sees a manifest — so existing agents stay accepted.
 * v15 -> v16: THE LEXICON CUT, and the one bump in this list that RESETS the
 * acceptance set. Every layer now speaks one vocabulary, so wire names moved:
 * the container is a `container` everywhere (`containerId`, `kind: "container"`,
 * `manifold://container/<id>`) and its `layout` field is its `discipline`, whose
 * values are `canvas` | `composition`; a PTY is a `terminal` everywhere
 * (`terminalId`, `TerminalInfo`, the `terminal_event` frame, `terminals` in
 * init/resync and in the agent `hello`) and `session` now names only a client
 * connection; the presence roster frame is `attendance`; published per-principal state is
 * `vantage`; a leaf's occupant and a placement's subject are both a `ref`
 * (`TileRef`, `PlacementRef`, denial `unknown_ref`); caps are
 * `containers:read` | `containers:write` | `scenes:write` | `terminals:spawn` |
 * `terminals:write`; every closed-enum wire literal is snake_case
 * (`canvas_item`, `no_self_embed`, `on_claim`); and the PTY environment carries
 * `MANIFOLD_CONTAINER`.
 *
 * The MACHINE wire is part of that move — `terminalId`, `terminals` on hello,
 * `MANIFOLD_CONTAINER` — so a v15 agent can neither be understood nor
 * understand this server. The set is therefore `{16}` and the upgrade is a
 * COORDINATED RESTART, sanctioned rather than accidental: stop the fleet, apply
 * DB migration 11 (it takes its own backup), start the server, then restart
 * every enrolled agent. An old agent dialing in is refused at machine-ws
 * version negotiation (close 4409) instead of silently exchanging frames whose
 * field names no longer exist.
 * v16 -> v17: THE EVENT PLANE (ADR 0012), session-channel only and ADDITIVE.
 * Three connection-level frames arrive — client `subscribe`/`unsubscribe`
 * ({ topics: ManifoldRef[] }) and server `event` ({ topic, kind, at, actor,
 * payload }) — and nothing existing changes shape: every v16 frame parses
 * byte-for-byte as it did, and a v16 client that never sends `subscribe`
 * receives no `event` and therefore observes a v16 server exactly. The plugin
 * manifest's reserved `contributes.events` gains its first consumer and its
 * `id` narrows from a local name to an event KIND (snake_case), which breaks no
 * manifest because no manifest in the tree declares an event and the wave that
 * writes the first one writes it in the narrowed spelling.
 *
 * The machine wire is BYTE-IDENTICAL: an agent holds PTYs and speaks
 * `AgentMessage`/`ServerToAgentMessage`, neither of which gained, lost or
 * renamed a field — `machine.ts` imports exactly one constant from the rest of
 * the protocol (`MAX_SESSION_BASE64_CHARS`, unchanged), and an agent never sees
 * a session frame or a manifest. So docs/CONTRACTS.md §Protocol and compatibility's unchanged-wire rule applies
 * verbatim: a bump that leaves the agent wire identical ADDS the new version
 * rather than resetting the set. The set is `{16, 17}` and NO fleet restart is
 * owed; an enrolled v16 agent keeps its terminals across this server deploy,
 * which is the exact outcome the invariant exists to protect.
 * v17 -> v18: CROSS-INSTANCE SHARING (ADR 0014), session-side additive and a
 * brand-new wire beside the existing two. `Principal` gained an OPTIONAL
 * `origin` — which instance a principal belongs to, absent ≡ this one — so
 * every v17 principal payload parses unchanged and a client that never meets a
 * remote peer observes a v17 server exactly. The instance channel
 * (`/ws/instance`, `instance.ts`) is a NEW wire rather than a change to an old
 * one: a guest instance dials a host with a share token, and the host answers
 * tickets that are ordinary attenuated tokens.
 *
 * The machine wire is BYTE-IDENTICAL. `AgentMessage` and
 * `ServerToAgentMessage` gained, lost and renamed nothing; an agent never sees
 * a `Principal`, and the liveness constants below were RENAMED
 * (`MACHINE_PING_INTERVAL_MS` -> `DIAL_PING_INTERVAL_MS`,
 * `AGENT_LIVENESS_TIMEOUT_MS` -> `DIAL_LIVENESS_TIMEOUT_MS`) without changing a
 * cadence, a deadline or a close code — two identifiers, zero bytes. So
 * docs/CONTRACTS.md §Protocol and compatibility's unchanged-wire rule applies verbatim: the set is `{16, 17, 18}` and
 * NO fleet restart is owed.
 * v18 -> v19: SESSION-CHANNEL LIVENESS (issue #55), and the one bump whose session
 * change REORIENTS a frame pair rather than adding one. The liveness pair on
 * `/ws/session` now points the way it points on every other dial: the server sends
 * `ping`, the client answers `pong`. The client's own keepalive ping and the server
 * `pong` that answered it are GONE — both spellings existed at v18, neither direction
 * did, so a v18 client on a v19 server would ignore every ping and be reaped two
 * intervals later. That is a lockout, which is why this is a bump rather than a quiet
 * addition: a stale tab is refused at the join with 4409 instead of churning.
 *
 * The machine wire is BYTE-IDENTICAL. `AgentMessage` and `ServerToAgentMessage` gained,
 * lost and renamed nothing, and an agent never sees a session frame. So docs/CONTRACTS.md §Protocol and compatibility's
 * unchanged-wire rule applies verbatim: the set is `{16, 17, 18, 19}` and NO fleet restart is
 * owed.
 * v19 -> v20: SESSION EXPIRY AND THE CREDENTIAL LIST (ADR 0019 §2-§3), additive-optional
 * on both halves it touches. `TokenGrant` gained an OPTIONAL `expiresAt` and
 * `MachineSummary` an OPTIONAL `revoked`; absent reproduces v19 exactly, because at v19 no
 * credential expired and no machine could be withdrawn. `AUTH_REFUSALS` publishes the two
 * words a credential refusal can be — `revoked`, which v19 already sent as a 4403 close
 * reason, and the new `expired` — so a lens can tell "come back with a fresh credential"
 * from "stop asking"; a client that switches on neither reads them as the prose it always
 * read. `core.access.listCredentials` and `core.machines.revoke` are new DOORS, which the
 * roster publishes and no version gates.
 *
 * The machine wire is BYTE-IDENTICAL. `AgentMessage` and `ServerToAgentMessage` gained,
 * lost and renamed nothing, and machine tokens are the deliberate expiry EXEMPTION — an
 * agent's credential is long-lived by design, so nothing an enrolled spoke holds changes
 * meaning across this deploy. So docs/CONTRACTS.md §Protocol and compatibility's unchanged-wire rule applies verbatim: the set is
 * `{16, 17, 18, 19, 20}` and NO fleet restart is owed.
 * v20 -> v21: THE DISCIPLINE ROSTER OPENS (#110, building the ruling ratified on #86).
 * `ContainerDisciplineSchema` stops enumerating `canvas` and `composition` and becomes a
 * bounded plugin-id-segment string, so a container discipline is a manifest contribution
 * (`contributes.disciplines`) instead of a wire enum. Every direction of this is a
 * LOOSENING or an ADDITION: every `Container` row that parsed at v20 parses at v21 with
 * the same meaning, every manifest written before the field existed composes unchanged,
 * and the two shipped disciplines say in `core.canvas`'s and `core.compositions`'
 * manifests exactly what `placement.ts` used to say in four literal tables.
 *
 * It is a bump rather than a quiet addition because the ANSWERS change shape in ways a
 * v20 reader would misread: `PLACEMENT_DENIAL_RULES` gains `unknown_discipline`, a word a
 * v20 client has no rendering for; `placementVocabulary().items` and `.canvasOps` no
 * longer carry `canvas`/`composition` rows, `.containers` is the closed FAMILY list
 * rather than a table of acceptances, and `.destinations` loses its `requires` column. A
 * client switching on those tables to predict legality would predict wrongly, and being
 * refused at the join with 4409 is the honest outcome.
 *
 * THE SAME BUMP also carries the carry aim's two missing fields (issue #66),
 * additive-optional on the session channel and nowhere else: `CarryAim` gained an
 * OPTIONAL `revision` — a content hash of the tile tree the producer resolved against,
 * so a viewer whose layout has skewed withholds a confidently wrong preview instead of
 * painting one — and the SERVER's gesture frame gained an OPTIONAL `aimOnly`, stamped on
 * the copy the server fans into the room a carry's aim addresses so that room reads the
 * aim without painting a ghost from another room's coordinates. Absent reproduces v20
 * exactly: at v20 no aim carried a revision (so every remote preview was unverifiable,
 * which is what absence still means) and no frame was ever fanned across rooms (so no
 * frame could be aim-only).
 *
 * The machine wire is BYTE-IDENTICAL under both halves of the bump. `AgentMessage` and
 * `ServerToAgentMessage` gained, lost and renamed nothing; `machine.ts` mentions no
 * container, no placement, no manifest, no gesture frame and no carry. So docs/CONTRACTS.md §Protocol and compatibility's
 * unchanged-wire rule applies verbatim: the set is `{16, 17, 18, 19, 20, 21}` and NO fleet
 * restart is owed — an enrolled v16 spoke keeps its terminals across this deploy.
 *
 * v21 -> v22: A TERMINAL IS BORN RUNNING A PROGRAM (issue #192), and the one bump so far
 * whose ADDITION touches the agent wire — the additive-optional rule of docs/CONTRACTS.md §Protocol and compatibility, applied for the
 * first time. `terminal_open` gained an OPTIONAL `program { argv }` and an OPTIONAL `env`
 * allowlist (upper-case POSIX keys, never the `MANIFOLD_` prefix, merged UNDER the four fixed
 * keys); `create` gained the same OPTIONAL `program`, and its `env` now carries the opener's
 * keys beside the minted ones — a record it always was, read by every agent since v16 with
 * the same loop. The session error vocabulary gained `unsupported`. The published input of
 * `core.terminals.open` gained both fields too: the gateway hands the door the frame's own
 * program and env before any create, so the policy door judges what will run.
 *
 * Absence reproduces v21 EXACTLY: a `create` without `program` is the byte-identical frame,
 * and the agent that receives it resolves `$SHELL` → `bash` → `sh` as it always did. The
 * agent's strict `create` parser means a v16–v21 agent would read a `program` key as a
 * malformed frame — so the v22 server refused program creation below v22 (`unsupported`).
 * An enrolled pre-v22 spoke therefore observed a v22 hub exactly as
 * it observed a v21 one, which is the additive-optional rule's test verbatim — every pre-bump frame
 * still parses, and the default for the absent field is the pre-bump behaviour — so 22 is
 * ADDED, the set is `{16, 17, 18, 19, 20, 21, 22}`, and NO fleet restart is owed. What a
 * pre-v22 spoke cannot do is run a program; that is a named refusal, not a lockout.
 *
 * The hub-ahead-of-fleet coupling holds with more force than usual here: a v22 AGENT in
 * front of a v21 hub is refused 4409 on every dial (the hub was built before 22 existed), so
 * the target hub must be upgraded before those binaries are installed. Publishing the
 * release alone does not authorize production promotion.
 *
 * v22 -> v23: TITLEBAR PRESENCE (issue #216). Session presence publishes the active
 * projection's canonical reference path and per-connection location snapshots, so every
 * ancestor titlebar can show the same participant without conflating sibling tabs. Element
 * contributions also declare per-discipline body/titlebar presentation (issue #222), and
 * contributed on-claim elements use the existing tile address regardless of their type.
 * Old browser decoders are strict: optional fields still require a session version bump.
 * Machine and instance frames are unchanged; both compatibility sets ADD 23.
 * Upgrade a hub before installing newer agent binaries; publication does not promote a hub.
 *
 * v23 -> v24: TERMINAL CONTINUITY (issue #278). A capable agent names its
 * independently supervised terminalHostId in hello and answers the new drain
 * request with its latched admission state. Both the hello field and HTTP
 * draining roster field are optional. The hub sends drain frames only to an
 * owner-naming agent; legacy machine frames remain byte-identical and keep
 * reconnecting with their retained inventories. Unsafe duplicate/incomplete
 * inventories are refused rather than interpreted as terminal destruction.
 * Machine compatibility ADDS 24; instance frames are unchanged and ADD 24.
 * Publish first, promote the target hub deliberately, then install the new
 * two-process agent topology. Publication never authorizes fleet activation.
 *
 * v24 -> v25: MACHINE ADDRESSES AND SHARED SETTINGS (issues #157, #158). The
 * `manifold://` grammar gains its eighth form, `machine/<machineId>`, so a
 * resolve answer, a trace target and a spotlight can name an enrolled spoke;
 * plugin setting declarations gain `scope: "workspace"` and the `enum` kind,
 * and `GET /api/settings` answers string values beside booleans. Old browser
 * decoders are strict, so the session version bumps. The machine wire is
 * byte-identical and a guest instance only ever receives container refs
 * (shares stay container-only, ADR 0014), so both compatibility sets ADD 25.
 * Upgrade a hub before installing newer agent binaries; publication does not
 * promote a hub.
 *
 * v25 -> v26: IMAGE-AWARE TERMINAL VIEWERS (issue #423). The VT stream and snapshots
 * now include graphics cell placement and parser continuation. A plain pre-v26 xterm
 * ignores image cursor effects and would misplace following text, not just omit pixels.
 * Refuse those browser sessions so the existing update/protocol-skew UI requests reload.
 * Machine and instance frame schemas are byte-identical, so both compatibility sets
 * ADD 26; no old spoke is forced off its hub. An already-running terminal host remains
 * its old implementation until safely replaced. Upgrade the target hub before installing
 * a v26 transport; this source change authorizes no production/fleet activation.
 *
 * v26 -> v27: GOVERNED MACHINE JOBS (ADR 0033). The machine wire adds optional
 * `hello.jobOwner` and `job_command`/`job_event` variants for governed execution.
 * Only protocol-27 agents may advertise job ownership or exchange governed-job frames;
 * protocol-26 terminal agents remain accepted for terminal service but never see job
 * commands and are refused if they advertise a job owner or send a job event. The
 * additive job extension preserves terminal compatibility, so machine acceptance ADDS
 * 27 and retains 16–26. The pre-v22 terminal-program guard remains unchanged.
 * Instance shares carry expanded closed resource/capability vocabularies, so the
 * separate instance acceptance set RESETS to `{27}`; federation peers require a
 * coordinated upgrade. This source change authorizes no production/fleet activation.
 *
 * v27 -> v28: PINNED RESOURCES AND SCOPED SERVICES. Governed jobs now require the
 * correlated input-receipt and service-authorization contracts, resource inventory,
 * and native service configuration/read messages. Protocol-27 transports remain
 * compatible only when they do not advertise a job owner; governed owners must
 * upgrade together with their transport before reconnecting. Existing workloads
 * remain owned by their independent owner while disconnected. Terminal-only wire
 * and federation resource vocabularies are unchanged, so their sets add 28.
 * This source change authorizes no hub activation or fleet replacement.
 *
 * v28 -> v29: INSTANCE-OWNED SERVICES. Governed owners gain durable service origins,
 * proved service readiness and bounded cross-owner service channels. Terminal-only
 * transports and federation vocabularies are unchanged. Governed owners and their
 * transports must upgrade together; occupied owners remain held until safely drained.
 *
 * v29 -> v30: EXPLICIT TERMINAL EXECUTION. Owners declare unconfined or governed
 * terminal admission independently of native job connectivity. Omitted declarations
 * never authorize an unconfined shell. This changes new-terminal semantics, so
 * machine acceptance resets to 30 and requires a coordinated hub/transport upgrade.
 * Retained IPC-v1 owners remain readable through new transports; their existing
 * terminals and governed requests keep their owners, but new unconfined terminals
 * require an IPC-v2 declaration. No owner is stopped to manufacture that declaration.
 * Native owner RPC is independently versioned at 29; unchanged native RPC must not
 * become incompatible merely because the hub or transport wire changes.
 * Federation frames and resource vocabularies are unchanged.
 *
 * v30 -> v31: MACHINE REPOSITORY FACTS (issue #529). The machine wire adds the
 * `repository_query`/`repository_fact` pair: one bounded absolute path in, one observation
 * out. A protocol-30 agent never receives the query — the hub checks the hello's version
 * and refuses `engine.machines.repository` by name for an older transport rather than
 * waiting out a timeout — so the v30 wire is byte-identical and machine acceptance ADDS 31
 * instead of resetting. The instance wire mentions no machine and is unchanged, so it adds
 * 31 too. No agent is restarted to gain the answer; a fleet upgrades when its operator says
 * so, and until then the door answers that the transport cannot be asked.
 *
 * v31 -> v32: DURABLE AGENTS (issue #578). Native terminal launch gains optional
 * run binding and private environment carriers. Absence preserves the old launch
 * semantics, so machine acceptance ADDS 32. The hub sends those carriers only to
 * transports at version 32 or newer and native owners at RPC version 35 or newer;
 * an older spoke refuses harness launch by name without receiving a credential.
 * Existing terminals and ordinary launches keep their prior semantics. Deploy
 * the hub first, then upgrade spokes when ready; no fleet restart is forced.
 * Agent/Run refs and harness manifests are session/HTTP vocabulary. Federation
 * frames are unchanged, so the instance acceptance set also adds 32.
 *
 * v32 -> v33: TERMINAL CWD AND RESTART (issue #583). Owners may advertise their
 * observed `cwd` and `terminalRestart` support. The hub only sends `terminal_restart`
 * when the transport is at least v33 and the current owner declared support; an older
 * transport or retained owner is refused as unsupported. Cwd/restart events are additive,
 * and absent cwd remains unknown. Machine acceptance ADDS 33. Session joins are
 * strictly 33 for cwd/restarted events and terminal summaries. Owner loss retains
 * exited tiles rather than removing their homes. IPC stays 2, and the unchanged
 * instance wire adds 33. No terminal host is stopped to acquire this feature.
 *
 * v33 -> v34: OWNER-SCOPED EVENTS (issue #601). Session event frames require the
 * originating plugin id, and clients interpreting a kind qualify it by that origin.
 * Machine and instance frames are unchanged; their acceptance sets add the shared
 * revision without dropping compatible transports or requiring a fleet restart.
 *
 * v34 -> v35: ROOMLESS SESSION OBSERVATION (issue #354). The session wire adds the
 * `observe`/`observed` handshake so a workspace-root browser can authenticate the existing
 * pooled socket, receive roster changes and hold event subscriptions without inventing a
 * room. Machine and instance frames are byte-identical, so both compatibility sets add 35.
 * The browser session remains strictly current and upgrades with the served SPA.
 *
 * v35 -> v36: TRUTHFUL TERMINAL READINESS (issue #203). The terminal owner may report one
 * monotonic readiness observation: an application's reserved OSC declaration or the
 * bash/zsh/readline bracketed-paste enable heuristic. Session terminal summaries gain required
 * nullable `readiness`, and `terminal_event` gains `kind:"ready"` with its evidence source.
 * Machine `terminal_ready` and advertised `readiness` are strictly additive: older transports
 * ignore the unknown event and older owners omit reconnect evidence, so machine compatibility
 * ADDS 36. Retained readiness also appears inside the terminal host's strict inventory, so the
 * terminal-host IPC bumps to version 3; a retained v2 host remains usable but cannot provide
 * readiness evidence until separately upgraded. The instance wire is unchanged and adds 36.
 *
 * v36 -> v37: FIRST-VIEWER TERMINAL FIT (issue #207). A tiled `terminal_open` may omit
 * geometry; the server publishes its tile first and starts the PTY only after the first
 * measured viewer sends `terminal_resize`. Element placement retains required opener-owned
 * geometry. Machine, terminal-host, and instance frames are unchanged, so compatible machine
 * and instance acceptance sets add 37 without restarting retained owners.
 *
 * v37 -> v38: BOUNDED ACTION RESULT PROJECTIONS (issue #796). Action discovery and HTTP
 * outcomes gain an optional declared projection, and the external action runner may deliver it
 * only under an exact trusted-launcher opt-in. Strict SDK and hardened-plugin consumers update
 * together; absent metadata and ordinary action calls retain their previous result semantics.
 * Machine, terminal-host and instance frames are unchanged, so both acceptance sets add 38.
 *
 * v38 -> v39: GOVERNED TERMINAL MATERIAL INPUTS (issue #800). Terminal runtimes may
 * carry the same sealed-output input bindings as ordinary jobs. Session and action
 * consumers update together; omission keeps the previous terminal behavior.
 * Machine, terminal-host and instance frames are unchanged. Native bound inputs
 * already require owner RPC 36, so compatible transports remain admitted.
 *
 * v39 -> v40: REVIEWED TEXT RESULT LEAVES (issue #809). Action result projections may
 * declare optional `textFields`, digest-pinned exact selected string-or-null leaves.
 * Omission preserves existing declarations, digests and primitive projection semantics.
 * Strict SDK/session consumers update together; hardened contract 5 retains older guests.
 * Machine, terminal-host, native-owner RPC and instance frames are unchanged, so both
 * acceptance sets add 40 without dropping compatible peers or restarting retained owners.
 *
 * v40 -> v41: SAME-INSTALLATION SERVICE BOOTSTRAP (issue #715). A job-scoped service
 * runtime may omit its installation revision only to resolve the invoking installation's
 * exact artifact, provider operation and resource identity. The hub projects contextual
 * policies out for older peers and refuses affected uses unless both machine transport
 * v41 and native owner RPC v37 support them. Existing revision-pinned policies and ordinary
 * work retain their semantics, so compatible machines remain admitted without a fleet
 * restart. The instance wire is unchanged and its acceptance set adds 41.
 *
 * v41 -> v42: TERMINAL SESSION CORRELATION AND BOUND READINESS (#814, #815).
 * TerminalRuntime and public terminal inventories carry optional machine-bound SessionRef
 * metadata; public jobs expose admission limits and authorized service descriptions expose
 * metering facts. Strict session and SDK consumers update together. These are hub projections,
 * not new machine, terminal-host, owner RPC or instance fields, so both acceptance sets add 42
 * without a coordinated fleet restart.
 *
 * v42 -> v43: NATIVE RUN-BOUND ACTION TOOLS (issue #769). Explicit operator approvals
 * and immutable Run selections reuse registered actions and bounded result publication.
 * Optional signed Run identity/expiry and ephemeral tool request/cancel/result frames
 * require machine v43 and owner RPC v41 before admission. Ordinary jobs remain unchanged;
 * hardened contract 6 adds trusted Run provenance while retaining older ordinary guests.
 * The machine and unchanged instance acceptance sets add 43.
 *
 * v43 -> v44: TERMINAL OWNER EXIT REASONS (issue #853). The machine `exited` frame gains
 * OPTIONAL `exitReason` (`owner_stopped` | `owner_oom_stopped`): the terminal host ended the
 * PTY on its destructive stop path, and says whether its own cgroup recorded a kernel OOM
 * kill just before. Older agents omit it and absence keeps the old meaning, an ordinary exit,
 * so machine acceptance ADDS 44 and keeps 30–43. An older hub parses `exited` strictly, but
 * it refuses a v44 transport at hello (4409) before one could be sent: upgrade the hub
 * before transports.
 * Session terminal summaries gain required nullable `exitReason`, which adds the hub-only
 * `owner_lost`, and the exited terminal event carries it; strict session consumers update
 * together. Terminal-host IPC stays 3: the host announces its stop with a new
 * `destructive_stop` event, which an older transport ignores as an unknown type, and never
 * adds the key to its own `exited`. The instance wire is unchanged and adds 44.
 *
 * v45: manifest release sources, verified plugin-update reviews and installer compatibility
 * observations extend the session/HTTP plugin vocabulary. Machine and instance messages and
 * adoption semantics are unchanged; both compatibility sets retain their older members.
 *
 * v46: EXPLICIT TERMINAL VIEWPORTS (issue #898). A tiled opener may supply its
 * own measured or virtual cols/rows instead of waiting for another viewer. Existing
 * message shapes are unchanged, but strict session clients update with this semantic
 * contract. Machine and instance frames are unchanged; older transports remain admitted.
 *
 * v47: portable React Worker entries and effective plugin execution mode extend the
 * session/HTTP vocabulary. Hardened contract 9 adds bounded React UI, mounted contexts,
 * event invalidations and narrow machine bridges. No machine, owner, terminal-host or
 * instance frame changes; existing transports remain admitted without a fleet restart.
 *
 * v48: LIVE PHYSICAL CORE INVENTORY (issue #939). Hello, the fleet bridge and machine
 * summaries gain optional `physicalCoreCount`: distinct online OS-visible package/core
 * identities, unknown when absent. Only the current admitted channel retains the fact.
 * Older transports omit it and remain admitted; machine acceptance adds 48. Upgrade the
 * hub before transports because older hubs parse hello strictly. Instance frames are
 * unchanged and add 48. Hardened contract 10 carries the optional inventory field;
 * hosts omit it for older guests whose inventory parser cannot understand it.
 *
 * v51: smallest active terminal viewports (issue #877). Session resize carries a mounted
 * viewport identity and desired geometry (null withdraws); transient sizing attribution
 * carries only connection references. Machine, native-owner and instance wires are unchanged.
 * Held Text/Files candidates are not reservations of integrated session revisions.
 *
 * v52: bounded direct-service money (issue #937). New monetary policy, invocation and charge
 * fields require both machine transport 52 and native owner RPC 44. Older transports remain
 * admitted for legacy work, but receive none of those policies or calls.
 *
 * v53: host-approved remote share recipients (issue #412). Tickets carry the actual
 * approved/requested remote subset and finite expiry; instance compatibility resets.
 * Machine frames and native-owner/terminal-host wires remain unchanged.
 *
 * v54: CORRELATED ACCOUNT-SHELL AUTHORITY AND LIVE WORKSPACE FEEDS (issues #956/#957).
 * Session connections publish authority_context for their actual credential and support
 * ID-only sync_subscriptions/subscriptions_synced ordering fences. Hardened contract 12
 * forwards those facts and the bounded fence to portable feeds; older strict Workers
 * retain their exact context projection. Machine, instance and retained-owner IPC are
 * unchanged, so their compatibility sets add 54 without requiring a fleet restart.
 * v55: SEALED NATIVE CREDENTIAL ENROLLMENT (issue #768, ADR 0059).
 * Optional signed owner key metadata and closed enrollment command/event modes require
 * both machine transport55 and owner RPC45. Earlier accepted spokes/owners retain ordinary
 * jobs and externally provisioned credential consumption; they receive no enrollment mode.
 * Session/HTTP discovery moves to55 for the separate host-only entry document. Federation
 * and the existing plugin/hardened ABI are unchanged, so their explicit sets add55.
 *
 * v56: ORDERED TERMINAL GEOMETRY (issue #879). Current session snapshots carry their own
 * geometry, and sparse geometry frames share the owner's output ordering. Optional machine
 * hello terminalGeometry selects new geometry_snapshot/request types only for capable owners.
 * Absent capability preserves legacy owners. IPC3 keeps every existing frame unchanged;
 * new-type support and geometry events are ignored by older transports. Instance wire and
 * native-owner RPC are unchanged; their supported peers do not require a fleet restart.
 *
 * v57: PARSER-COMPLETION TERMINAL DELIVERY (issue #880). Session attach/detach identify
 * each mounted viewport; its stream carries an independent delivery incarnation and ordinal,
 * and completed consumption cumulatively credits bounded delivery. Snapshot recovery discloses
 * potentially skipped output/history. The trusted plugin/SDK terminal ABI changes: old bundles
 * must rebuild rather than receive a shared-view or socket-receipt ACK shim. Plugin bundle
 * acceptance resets to 57. Machine, native-owner, hardened, terminal-host and instance messages
 * are unchanged; machine and instance acceptance add 57 without requiring a fleet restart.
 *
 * v58: DECLARATIVE CREATORS (issue #1079). Plugin manifests gain the optional
 * `contributes.creators` (offers the shell runs from one picker), and `core.index.createFolder`
 * answers the created `folderId` beside the index. Old browser and SDK decoders are strict, so
 * the session version bumps and strict consumers update together. No released 57 bundle has
 * been proven against this host, so plugin bundle acceptance resets to 58. Machine,
 * native-owner, hardened, terminal-host and instance messages are unchanged; machine and
 * instance acceptance add 58 without requiring a fleet restart.
 */
export const MACHINE_PROTOCOL_COMPAT_VERSIONS: ReadonlySet<number> = new Set([
  30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 51, 52, 53, 54, 55,
  56, 57, 58,
]);

/**
 * The first machine protocol that carries `repository_query`/`repository_fact` (issue #529).
 * A transport below it is asked NOTHING: it would ignore the frame as an unknown type, and
 * an unanswerable question must be refused by name rather than waited out.
 */
export const MACHINE_REPOSITORY_PROTOCOL_VERSION = 31;

/** A pre-v33 transport must never receive the restart command it cannot answer. */
export const TERMINAL_RESTART_PROTOCOL_VERSION = 33;

/** Ordered terminal geometry additionally requires explicit support from the retained owner. */
export const TERMINAL_GEOMETRY_PROTOCOL_VERSION = 56;

/** Older machine parsers must never receive a contextual self-provider runtime policy. */
export const MACHINE_SELF_PROVIDER_PROTOCOL_VERSION = 41;

/** Tool requests require the actual machine transport as well as owner RPC 41. */
export const MACHINE_AGENT_TOOLS_PROTOCOL_VERSION = 43;

/** Strict transport parsers must understand the optional monetary policy/command/reply fields. */
export const MACHINE_DIRECT_SERVICE_ACCOUNTING_PROTOCOL_VERSION = 52;

/** Older strict machine parsers must never receive sealed-enrollment commands or metadata. */
export const MACHINE_CREDENTIAL_ENROLLMENT_PROTOCOL_VERSION = 55;

/**
 * Instance-channel acceptance set, and a SEPARATE set on purpose (ADR 0014).
 *
 * A guest instance is long-lived in the same way an agent is, so its wire needs
 * the same negotiation discipline — and it is not the agent's wire. Pointing the
 * instance channel at the machine set would mean an agent-wire reset silently
 * locking out federated instances that never spoke that wire, and the reverse:
 * an instance-frame change forcing a fleet of PTY agents to restart. Two wires,
 * two sets, one discipline (docs/CONTRACTS.md §Protocol and compatibility, applied per wire).
 *
 * v18: the version that introduces the wire.
 * v19: session-channel only — the liveness pair reoriented on `/ws/session`. The
 * instance wire is byte-identical (a guest never sees a session frame), so docs/CONTRACTS.md §Protocol and compatibility's unchanged-wire rule ADDS the version rather than resetting the set, and a v18 guest
 * instance keeps its dial across this deploy.
 * v20: session/HTTP only — credential expiry and the credential list (ADR 0019). A guest
 * instance holds a SHARE secret, which is not a token row and carries no expiry, so the
 * instance wire is byte-identical again and the version is ADDED.
 * v21: session/HTTP and manifest only — the discipline roster opens (#110) and the carry
 * aim gains its revision stamp + server aim-only fan (issue #66). `instance.ts` mentions
 * no container, no placement, no manifest and no gesture frame, and a guest holds a share
 * secret rather than a scene, so the instance wire is byte-identical a third time and the
 * version is ADDED.
 * v22: session and machine channels only — a terminal born running a program (issue #192).
 * `instance.ts` mentions no terminal, no PTY and no `create`, and a guest holds a share
 * secret rather than a machine token, so the instance wire is byte-identical a fourth time
 * and the version is ADDED.
 * v23: session presence and element presentation (issues #216/#222); instance wire unchanged.
 * v24: terminal-host identity and drain on the machine channel; instance wire unchanged.
 * v25: machine references and shared settings on the session/HTTP wire (#157/#158); a guest
 * never receives a machine ref, so the instance wire is unchanged.
 * v26: image-aware terminal viewers; instance frames remain unchanged.
 * v27: governed jobs expand the closed share resource/capability vocabularies (ADR 0033);
 * instance compatibility resets to protocol 27. v28 through v48, v51 and v52 leave that wire unchanged.
 * v53 requires explicit host-recipient admission and ticket subset/expiry fields; reset
 * the instance set so old ambient-admission peers cannot resume through the new contract.
 * v54 adds scoped session authority and leaves that host-recipient instance wire unchanged.
 * v58 adds manifest creators on the session/HTTP wire; the instance wire is unchanged.
 */
export const INSTANCE_PROTOCOL_COMPAT_VERSIONS: ReadonlySet<number> = new Set([
  53, 54, 55, 56, 57, 58,
]);

/**
 * Liveness cadence for every DIALED pipe (CONTRACTS.md): the machine channel, the
 * instance channel and the session channel all have a peer that dialed OUT to a server,
 * and all three answer the same question — is this transport still real? The dialed-in
 * side pings on this interval and closes (4008) when a ping is still unanswered as the
 * next one fires, bounding offline detection at two intervals. The dialing side closes
 * and re-dials when it hears NOTHING for DIAL_LIVENESS_TIMEOUT_MS — a healthy but idle
 * connection still carries pings, so silence longer than two intervals plus grace means
 * the transport is a phantom (dead TCP with no RST, e.g. a proxy swallowed the close).
 *
 * WHICH SIDE PINGS is not a coin flip, and the browser is the case that proves it: a
 * background tab's timers are throttled to roughly one firing per minute, so a reap
 * keyed on pings the DIALING side generates would close live tabs, while answering an
 * inbound ping rides the message event and is throttled by nothing. The side that reaps
 * is the side that pings; the side that re-dials is the side that watches for silence
 * (issue #55). A late watchdog only detects late, which is harmless — an eager reap is
 * not.
 *
 * Named for the DISCIPLINE rather than for one of its occupants, which is the
 * lesson wave 3 taught: these were `MACHINE_PING_INTERVAL_MS` and
 * `AGENT_LIVENESS_TIMEOUT_MS` while the machine channel was the only dial, and
 * the second dial is what exposed the name as an accident. One scheme, one pair
 * of constants, every dial (docs/CONTRACTS.md §One authoritative implementation).
 */
export const DIAL_PING_INTERVAL_MS = 30_000;
export const DIAL_LIVENESS_TIMEOUT_MS = DIAL_PING_INTERVAL_MS * 2 + 15_000;
